import { Button, Input, Card, Form, Upload, Typography, Grid, Spin, Space } from "antd";
import { Upload as UploadIcon, FileText, ArrowLeft } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import useFileUpload from "../../hooks/useFileUpload";
import useFileProcess from "../../hooks/useFileProcess";
import ProgressModal from "../ProgressModal";
import * as XLSX from "xlsx";
import { useMemo, useState } from "react";
import { clearUploadState } from "../../store/slices/fileSlice";

const { Dragger } = Upload;
const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

const FileUploadAndProcessForm = () => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const screens = useBreakpoint();

  const [sheetNames, setSheetNames] = useState(null);
  const [selectedSheet, setSelectedSheet] = useState(null);
  const [localFileMeta, setLocalFileMeta] = useState(null);
  const [parsing, setParsing] = useState(false);

  const {
    loading,
    error,
    uploadedFilePath,
    processing,
    processError,
    activeStep,
    totalSteps,
  } = useSelector((state) => state.file);

  const { handleFileUpload } = useFileUpload(form, dispatch);
  const { handleFileProcess } = useFileProcess(dispatch, navigate);
  const MAX_FILE_SIZE_MB = 25;

  const handleRemoveUploaded = () => {
    // clear redux upload state
    dispatch(clearUploadState());
    // clear local UI state
    setLocalFileMeta(null);
    setSheetNames(null);
    setSelectedSheet(null);
  };

  const uploadProps = useMemo(
    () => ({
      multiple: false,
      maxCount: 1,
      accept: ".csv,.xlsx,.xls,.xlsb",
      showUploadList: false,
      disabled: loading || processing || parsing,

      beforeUpload: async (file) => {
        const isLt25MB = file.size / 1024 / 1024 <= MAX_FILE_SIZE_MB;
        if (!isLt25MB) {
          toast.error(`File must be smaller than ${MAX_FILE_SIZE_MB}MB!`);
          return Upload.LIST_IGNORE;
        }

        // set immediate local meta for UX
        setLocalFileMeta({ name: file.name, size: file.size });

        const lower = (file.name || "").toLowerCase();
        const looksLikeExcel =
          lower.endsWith(".xlsx") ||
          lower.endsWith(".xls") ||
          lower.endsWith(".xlsb");

        if (!looksLikeExcel) {
          // Not an Excel workbook — proceed normally
          setSheetNames(null);
          setSelectedSheet(null);
          return false;
        }

        setParsing(true);
        try {
          const ab = await file.arrayBuffer();
          // read workbook metadata (fast)
          const wb = XLSX.read(ab, {
            type: "array",
            bookFiles: false,
            bookProps: false,
          });
          const names = wb?.SheetNames ?? [];

          if (names.length > 1) {
            setSheetNames(names);
            setSelectedSheet(names[0]);
            toast.info(
              "File contains multiple sheets — now taking the first sheet."
            );
          } else if (names.length === 1) {
            setSheetNames(names);
            setSelectedSheet(names[0]);
          } else {
            setSheetNames([]);
            setSelectedSheet(null);
          }
        } catch (err) {
          console.error("Failed to parse workbook in beforeUpload:", err);
          toast.warn(
            "Could not inspect Excel sheets in the browser. Proceeding with upload."
          );
          setSheetNames(null);
          setSelectedSheet(null);
        } finally {
          setParsing(false);
        }

        // return false to keep manual upload flow
        return false;
      },

      onChange: handleFileUpload,
    }),
    [loading, processing, parsing, handleFileUpload]
  );

  const handleFinish = async (values) => {
    if (!uploadedFilePath) {
      toast.error("Please upload a file first.");
      return;
    }

    try {
      await handleFileProcess(uploadedFilePath, {
        ...values,
        sheetName: selectedSheet,
      });
    } catch (err) {
      toast.error(err || "Processing failed", {
        duration: 10000, // stays visible for 10 seconds
      });
    }
  };

  const onBack = () => navigate("/dashboard");

  return (
    <>
      {/* <BackButton onBack={onBack} /> */}
      <PageHeader screens={screens} />
      <ProgressModal
        loading={loading}
        processing={processing}
        activeStep={activeStep}
        totalSteps={totalSteps}
        disableClose={!!(loading || processing)}
        onClose={() => {}}
      />
      <UploadCard>
        <UploadForm
          form={form}
          onFinish={handleFinish}
          uploadProps={uploadProps}
          uploadedFilePath={uploadedFilePath}
          error={error}
          processing={processing}
          loading={loading}
          processError={processError}
          // new props for UI feedback
          parsing={parsing}
          sheetNames={sheetNames}
          selectedSheet={selectedSheet}
          localFileMeta={localFileMeta}
          onRemoveUpload={handleRemoveUploaded}
        />
      </UploadCard>
    </>
  );
};

const BackButton = ({ onBack }) => (
  <span className="top-7 left-5 bg-[#141c31]/70 backdrop-blur-md p-4 rounded-full">
    <Button
      type="text"
      icon={<ArrowLeft className="w-5 h-5 mt-1" />}
      onClick={onBack}
      className="font-medium !text-white !text-xl mt-2"
    >
      Back to Models
    </Button>
  </span>
);

const PageHeader = ({ screens }) => (
  <div className="relative text-center mb-8">
    <Title
      level={2}
      className="!text-white font-bold !mb-2 font-mono text-4xl"
      style={{ fontSize: screens.xs ? "1.5rem" : "2rem" }}
    >
      Intelligent Data Processing <span className="bg-gradient-to-r from-blue-400 via-cyan-400 to-blue-500 bg-clip-text text-transparent">Hub</span>
    </Title>
    <Text className="text-gray-200 text-lg font-semibold font-serif">
      Upload your datasets and let our advanced analytics engines transform
      raw data into actionable insights
    </Text>
  </div>
);

const UploadCard = ({ children }) => (
  <Card
    title={
      <div className="flex items-center gap-2 text-white">
        <FileText className="w-5 h-5" />
        <span>Upload New File</span>
      </div>
    }
    className="bg-[#141c31] w-full max-w-full sm:max-w-2xl md:max-w-4xl lg:max-w-6xl mx-auto rounded-2xl"
  >
    <div className="p-4 sm:p-6">{children}</div>
  </Card>
);

const UploadForm = ({
  form,
  onFinish,
  uploadProps,
  uploadedFilePath,
  error,
  processing,
  loading,
  processError,
  parsing,
  sheetNames,
  selectedSheet,
  localFileMeta,
  onRemoveUpload,
}) => (
  <Form form={form} layout="vertical" onFinish={onFinish}>
    <FormInputs />
    <FileUploader
      uploadProps={uploadProps}
      uploadedFilePath={uploadedFilePath}
      error={error}
      parsing={parsing}
      sheetNames={sheetNames}
      selectedSheet={selectedSheet}
      localFileMeta={localFileMeta}
      onRemoveUpload={onRemoveUpload}
    />
    <SubmitButton processing={processing} loading={loading} uploadedFilePath={uploadedFilePath} />
    {processError && <p className="text-red-500 mt-2">Submit error: {processError}</p>}
  </Form>
);

const FormInputs = () => (
  <>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <FormItem name="fileName" label="File Name" placeholder="Enter file name" />
      <FormItem name="processName" label="Process" placeholder="Enter Process Name" />
    </div>
    <FormItem name="subProcess" label="Sub process" placeholder="Enter Sub Process Name" />
  </>
);

const FormItem = ({ name, label, placeholder }) => (
  <Form.Item label={<span className="text-gray-200 text-base">{label}</span>} name={name}>
    <Input
      placeholder={placeholder}
      className="h-12 bg-gray-50 border-gray-200 rounded-lg"
    />
  </Form.Item>
);

// helper for nice file size
const formatBytes = (bytes) => {
  if (!bytes && bytes !== 0) return "";
  if (bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

const FileUploader = ({ uploadProps, uploadedFilePath, error, parsing, sheetNames, selectedSheet, localFileMeta, onRemoveUpload }) => (
  <Form.Item label={<span className="text-gray-200 text-base">Upload Dataset</span>} required>
    <Dragger {...uploadProps} className="rounded-lg border-dashed border-slate-600 bg-slate-800/30 py-6 sm:py-10">
      <p className="flex justify-center">
        <UploadIcon className="w-10 h-10 sm:w-12 sm:h-12 text-gray-200" />
      </p>
      <p className="text-base sm:text-lg text-gray-200">
        Click or drag file to this area to upload
      </p>
      <p className="text-gray-200 text-sm sm:text-base">
        File uploads immediately after selection.
      </p>
    </Dragger>

    {/* UX feedback for parsing / sheet selection / local meta */}
    <div className="mt-3">
      {parsing && (
        <div className="flex items-center gap-3">
          <div style={{ marginBottom: 12 }}>
            <Spin tip="Parsing workbook..." />
          </div>
          <Text className="text-gray-200">Inspecting workbook — reading sheets…</Text>
        </div>
      )}

      {!parsing && localFileMeta && (
        <div className="mt-2">
          <Space direction="vertical" size={2}>
            <Text className="text-gray-200">File: <span className="font-mono text-green-300">{localFileMeta.name}</span></Text>
            <Text className="text-gray-200">Size: <span className="font-mono text-gray-300">{formatBytes(localFileMeta.size)}</span></Text>
            {Array.isArray(sheetNames) && sheetNames.length > 1 && (
              <Text className="text-yellow-300">Selected sheet: <span className="font-mono">{selectedSheet}</span> — file has {sheetNames.length} sheets.</Text>
            )}
            {Array.isArray(sheetNames) && sheetNames.length === 1 && (
              <Text className="text-gray-200">Selected sheet: <span className="font-mono">{selectedSheet}</span></Text>
            )}
            {sheetNames && sheetNames.length === 0 && (
              <Text className="text-red-400">No sheets detected in workbook.</Text>
            )}
          </Space>
        </div>
      )}

      {uploadedFilePath && (
        <div className="mt-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <p className="text-green-600 text-base break-words break-all">
            Uploaded ✓ filePath: <span className="font-mono">{uploadedFilePath}</span>
          </p>
          <Button size="small" danger onClick={onRemoveUpload} className="!w-full sm:!w-auto">
            Remove file
          </Button>
        </div>
      )}
      {error && <p className="text-red-500 mt-2">Upload error: {error}</p>}
    </div>
  </Form.Item>
);

const SubmitButton = ({ processing, loading, uploadedFilePath }) => (
  <Button
    type="primary"
    htmlType="submit"
    className="w-full text-white bg-green-600 hover:bg-green-700 disabled:!text-white disabled:!bg-blue-800 font-serif"
    loading={processing || loading}
    disabled={!uploadedFilePath || loading}
  >
    {processing ? "Submitting..." : "Submit"}
  </Button>
);

export default FileUploadAndProcessForm;